import { useEffect, useState } from 'react'
import { PULSE_COLORS, QA_SCORE_PENALTIES, QA_SCORE_THRESHOLDS, SPRINT_END_DATES } from './pulseConfig.js'

function computeQaScore(s) {
    let penalty = 0
    penalty += s.hotfixCount * QA_SCORE_PENALTIES.HOTFIX_RELEASE
    penalty += s.patchCount * QA_SCORE_PENALTIES.PATCH_RELEASE
    const defectRate = s.completedCount > 0 ? s.bugsInSprint / s.completedCount : 0
    if (defectRate > QA_SCORE_PENALTIES.DEFECT_RATE_HIGH_THRESHOLD) {
        penalty += QA_SCORE_PENALTIES.DEFECT_RATE_HIGH_PENALTY
    } else if (defectRate > QA_SCORE_PENALTIES.DEFECT_RATE_LOW_THRESHOLD) {
        penalty += QA_SCORE_PENALTIES.DEFECT_RATE_LOW_PENALTY
    }
    const completionRate = s.committedCount > 0 ? s.completedCount / s.committedCount : 1
    if (completionRate < QA_SCORE_PENALTIES.COMPLETION_RATE_CRITICAL_THRESHOLD) {
        penalty += QA_SCORE_PENALTIES.COMPLETION_RATE_CRITICAL_PENALTY
    } else if (completionRate < QA_SCORE_PENALTIES.COMPLETION_RATE_LOW_THRESHOLD) {
        penalty += QA_SCORE_PENALTIES.COMPLETION_RATE_LOW_PENALTY
    }
    return Math.max(0, 100 - penalty)
}

function buildInsights(s) {
    const insights = []
    if (s.hotfixCount > 0) {
        insights.push({
            emoji: '🔥',
            text: `${s.hotfixCount} hotfix release${s.hotfixCount > 1 ? 's' : ''} (-${s.hotfixCount * QA_SCORE_PENALTIES.HOTFIX_RELEASE} pts)`,
            severity: 'bad',
        })
    } else {
        insights.push({ emoji: '🛡️', text: 'Zero hotfixes — production is stable', severity: 'good' })
    }
    const defectRate = s.completedCount > 0 ? s.bugsInSprint / s.completedCount : 0
    if (defectRate > QA_SCORE_PENALTIES.DEFECT_RATE_HIGH_THRESHOLD) {
        insights.push({
            emoji: '🐛',
            text: `High defect density: ${Math.round(defectRate * 100)}% bugs-to-stories (-${QA_SCORE_PENALTIES.DEFECT_RATE_HIGH_PENALTY} pts)`,
            severity: 'bad',
        })
    } else if (defectRate > QA_SCORE_PENALTIES.DEFECT_RATE_LOW_THRESHOLD) {
        insights.push({
            emoji: '🐛',
            text: `Elevated defect density: ${Math.round(defectRate * 100)}% bugs-to-stories (-${QA_SCORE_PENALTIES.DEFECT_RATE_LOW_PENALTY} pts)`,
            severity: 'warn',
        })
    } else {
        insights.push({
            emoji: '✅',
            text: `Defect density is healthy at ${Math.round(defectRate * 100)}%`,
            severity: 'good',
        })
    }
    const completionRate = s.committedCount > 0 ? s.completedCount / s.committedCount : 1
    if (completionRate < QA_SCORE_PENALTIES.COMPLETION_RATE_CRITICAL_THRESHOLD) {
        insights.push({
            emoji: '📉',
            text: `Only ${Math.round(completionRate * 100)}% stories completed vs committed (-${QA_SCORE_PENALTIES.COMPLETION_RATE_CRITICAL_PENALTY} pts)`,
            severity: 'bad',
        })
    } else if (completionRate < QA_SCORE_PENALTIES.COMPLETION_RATE_LOW_THRESHOLD) {
        insights.push({
            emoji: '📊',
            text: `${Math.round(completionRate * 100)}% completion rate — room to improve (-${QA_SCORE_PENALTIES.COMPLETION_RATE_LOW_PENALTY} pts)`,
            severity: 'warn',
        })
    } else {
        insights.push({
            emoji: '🚀',
            text: `Strong delivery: ${Math.round(completionRate * 100)}% completion rate`,
            severity: 'good',
        })
    }
    return insights
}

function scoreColor(score) {
    if (score >= QA_SCORE_THRESHOLDS.GREEN)
        return { ring: '#22C55E', glow: 'rgba(34,197,94,0.35)', text: '#A7F3D0', bg: '#064e3b', label: 'Healthy' }
    if (score >= QA_SCORE_THRESHOLDS.YELLOW)
        return { ring: '#EAB308', glow: 'rgba(234,179,8,0.35)', text: '#FDE68A', bg: '#78350f', label: 'Caution' }
    return { ring: '#EF4444', glow: 'rgba(239,68,68,0.35)', text: '#FECDD3', bg: '#7f1d1d', label: 'At Risk' }
}

const INSIGHT_TEXT = {
    good: '#34d399',
    warn: '#fbbf24',
    bad: '#f87171',
}

function formatDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export default function SprintScoreGauge({ sprint, loading, headerAction }) {
    const [animatedScore, setAnimatedScore] = useState(0)
    const score = sprint ? computeQaScore(sprint) : 0
    const colors = scoreColor(score)
    const insights = sprint ? buildInsights(sprint) : []

    useEffect(() => {
        if (!sprint) return
        const target = computeQaScore(sprint)
        let current = 0
        const step = Math.max(1, Math.ceil(target / 40))
        const timer = setInterval(() => {
            current = Math.min(current + step, target)
            setAnimatedScore(current)
            if (current >= target) clearInterval(timer)
        }, 20)
        return () => clearInterval(timer)
    }, [sprint])

    const size = 120
    const strokeWidth = 9
    const radius = (size - strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const progress = circumference - (animatedScore / 100) * circumference

    if (loading || !sprint) {
        return (
            <div
                style={{
                    background: PULSE_COLORS.cardBg,
                    borderRadius: '12px',
                    padding: '16px',
                    border: `1px solid ${PULSE_COLORS.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '140px',
                }}
            >
                <div
                    style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        background: PULSE_COLORS.bgInner,
                        animation: 'pulse 2s ease-in-out infinite',
                    }}
                />
            </div>
        )
    }

    const completionPct =
        sprint.committedCount > 0 ? Math.round((sprint.completedCount / sprint.committedCount) * 100) : 0
    const sprintEndDate = SPRINT_END_DATES[sprint.sprintNumber] ?? sprint.endDate
    const isActive = sprint.state === 'active'

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '16px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}
            >
                <h3
                    style={{
                        color: PULSE_COLORS.text,
                        fontWeight: 600,
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    QA Score
                    <span style={{ color: PULSE_COLORS.textSubtle }}>·</span>
                    {sprint.label}
                    {isActive && (
                        <span style={{ position: 'relative', display: 'inline-block', width: '8px', height: '8px' }}>
                            <span
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '50%',
                                    backgroundColor: colors.ring,
                                    opacity: 0.5,
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                }}
                            />
                            <span
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: colors.ring,
                                }}
                            />
                        </span>
                    )}
                    <span style={{ fontSize: '10px', color: PULSE_COLORS.textMuted, fontWeight: 400 }}>
                        ends {formatDate(sprintEndDate)}
                    </span>
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                        style={{
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontSize: '10px',
                            fontWeight: 700,
                            backgroundColor: colors.bg,
                            color: colors.text,
                        }}
                    >
                        {colors.label}
                    </span>
                    {headerAction}
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flexShrink: 0, width: size, height: size }}>
                    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke={PULSE_COLORS.border}
                            strokeWidth={strokeWidth}
                        />
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke={colors.ring}
                            strokeWidth={strokeWidth}
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={progress}
                            style={{
                                transition: 'stroke-dashoffset 0.8s ease-out',
                                filter: `drop-shadow(0 0 6px ${colors.glow})`,
                            }}
                        />
                    </svg>
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <span style={{ fontSize: '22px', fontWeight: 900, color: colors.ring, lineHeight: 1 }}>
                            {animatedScore}
                        </span>
                        <span
                            style={{
                                fontSize: '9px',
                                fontWeight: 600,
                                color: PULSE_COLORS.textMuted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            QA Score
                        </span>
                    </div>
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, auto)',
                        columnGap: '20px',
                        rowGap: '6px',
                    }}
                >
                    <Metric label="Points Done" value={sprint.completedPoints} />
                    <Metric label="Completion" value={`${completionPct}%`} />
                    <Metric
                        label="Bugs"
                        value={
                            <>
                                {sprint.bugsInSprint}{' '}
                                <span style={{ fontSize: '12px', fontWeight: 400, color: PULSE_COLORS.textMuted }}>
                                    / {sprint.bugsCompleted} fixed
                                </span>
                            </>
                        }
                    />
                    <Metric
                        label="Releases"
                        value={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '16px' }}>
                                <span style={{ color: sprint.hotfixCount > 0 ? '#EF4444' : '#10B981' }}>
                                    {sprint.hotfixCount}
                                </span>
                                <span style={{ fontSize: '11px', fontWeight: 400, color: PULSE_COLORS.textMuted }}>
                                    hotfix
                                </span>
                                <span style={{ color: PULSE_COLORS.textSubtle }}>|</span>
                                <span style={{ color: PULSE_COLORS.text }}>{sprint.patchCount}</span>
                                <span style={{ fontSize: '11px', fontWeight: 400, color: PULSE_COLORS.textMuted }}>
                                    patch
                                </span>
                            </span>
                        }
                    />
                    <Metric label="Active Devs" value={sprint.activeDevs} />
                    <Metric label="Cycle Time" value={`${sprint.avgCycleTimeDays}d`} />
                </div>

                <div
                    style={{
                        flexShrink: 0,
                        borderLeft: `1px solid ${PULSE_COLORS.border}`,
                        paddingLeft: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                    }}
                >
                    {insights.map((insight, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span style={{ fontSize: '13px', lineHeight: 1, marginTop: '2px' }}>{insight.emoji}</span>
                            <span style={{ fontSize: '11px', lineHeight: 1.25, color: INSIGHT_TEXT[insight.severity] }}>
                                {insight.text}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

function Metric({ label, value }) {
    return (
        <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: PULSE_COLORS.text }}>{value}</div>
            <div
                style={{
                    fontSize: '9px',
                    color: PULSE_COLORS.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                }}
            >
                {label}
            </div>
        </div>
    )
}
